import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function ClickCVPrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            Castle Point Innovations LLC ("we," "our," or "us") operates ClickCV, an AI-powered
            resume builder available as a Chrome browser extension. ClickCV helps users generate
            tailored resumes by analyzing job descriptions from supported job boards, including
            LinkedIn, Indeed, CareerBuilder, and ZipRecruiter.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and protect your information
            when you use the ClickCV extension and related services. By installing or using ClickCV,
            you agree to the practices described in this policy.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following categories of information:</p>
          <p><strong>Account Information</strong></p>
          <ul>
            <li>Google OAuth credentials, including your email address and profile information</li>
            <li>Email addresses used for account registration and communication</li>
          </ul>
          <p><strong>Resume and Career Data</strong></p>
          <ul>
            <li>Resumes, work history, skills, education, and other career-related content you provide</li>
            <li>Job descriptions and job listing data extracted from supported job board pages</li>
            <li>Generated resumes and associated metadata</li>
          </ul>
          <p><strong>Payment Information</strong></p>
          <ul>
            <li>Subscription and payment details processed through Stripe, including billing information and transaction history</li>
          </ul>
          <p><strong>Usage Data</strong></p>
          <ul>
            <li>Resume generation metrics, such as the number of resumes created and features used</li>
            <li>Referral information, including how you discovered or were referred to ClickCV</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Browser Permissions',
      content: (
        <>
          <p>ClickCV requires the following Chrome browser permissions to function:</p>
          <ul>
            <li>
              <strong>Storage:</strong> Used to save your preferences, session data, and cached
              information locally within the extension.
            </li>
            <li>
              <strong>Identity and Identity.email:</strong> Used to authenticate your account via
              Google OAuth and retrieve your email address for sign-in purposes.
            </li>
            <li>
              <strong>Content Scripts on Job Boards:</strong> ClickCV injects content scripts on
              LinkedIn, Indeed, CareerBuilder, and ZipRecruiter to read job descriptions and listing
              details from the pages you visit. These scripts only activate on the specified job
              board domains and do not collect data from other websites.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li>To authenticate your identity and manage your account</li>
            <li>To generate tailored resumes based on your work history and target job descriptions</li>
            <li>To process payments and manage your subscription</li>
            <li>To improve and optimize the resume generation experience</li>
            <li>To communicate with you about your account, updates, and support inquiries</li>
            <li>To track referral activity and usage metrics for service improvement</li>
            <li>To comply with legal obligations and enforce our terms of service</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>
            ClickCV relies on the following third-party services to deliver its functionality. Each
            service has its own privacy policy governing how it handles your data:
          </p>
          <ul>
            <li>
              <strong>Firebase (Authentication and Firestore):</strong> Used for user authentication
              and secure storage of account data, resumes, and application records.
            </li>
            <li>
              <strong>Google OAuth:</strong> Used to authenticate users via their Google accounts.
            </li>
            <li>
              <strong>Stripe:</strong> Used to process payments and manage subscriptions. We do not
              store full credit card numbers on our servers; payment data is handled directly by
              Stripe.
            </li>
            <li>
              <strong>Google Generative AI (Gemini API):</strong> Used to power AI-driven resume
              generation and content tailoring.
            </li>
            <li>
              <strong>OpenAI:</strong> Used as an additional AI provider for resume content
              generation and optimization.
            </li>
            <li>
              <strong>Google Cloud Run:</strong> Used to host backend services that support the
              extension's functionality.
            </li>
          </ul>
          <p>
            We only share the minimum data necessary with each third-party service to provide the
            features described in this policy.
          </p>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            Your data is stored securely using Firebase Firestore with access controls and
            encryption in transit. Backend services are hosted on Google Cloud Run with standard
            security configurations.
          </p>
          <p>
            While we implement commercially reasonable measures to protect your information, no
            method of electronic storage or transmission is completely secure. We cannot guarantee
            absolute security of your data.
          </p>
        </>
      ),
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>
            We retain your personal information for as long as your account remains active or as
            needed to provide you with our services. If you request account deletion, we will remove
            your personal data from our systems within a reasonable timeframe, except where retention
            is required by law or for legitimate business purposes such as resolving disputes or
            enforcing our agreements.
          </p>
          <p>
            Payment records processed through Stripe may be retained in accordance with Stripe's
            data retention policies and applicable financial regulations.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate or incomplete data</li>
            <li>Request deletion of your personal data</li>
            <li>Object to or restrict certain processing of your information</li>
            <li>Request a portable copy of your data</li>
            <li>Withdraw consent where processing is based on consent</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the information provided in the
            Contact Us section below. We will respond to your request within the timeframe required
            by applicable law.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            ClickCV is not intended for use by individuals under the age of 13. We do not knowingly
            collect personal information from children under 13. If we become aware that we have
            collected data from a child under 13, we will take steps to delete that information
            promptly. If you believe a child has provided us with personal data, please contact us
            immediately.
          </p>
        </>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            services, or applicable laws. When we make material changes, we will update the "Last
            Updated" date at the top of this page. We encourage you to review this policy
            periodically. Your continued use of ClickCV after any changes constitutes acceptance of
            the updated policy.
          </p>
        </>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our
            data practices, please contact us at:
          </p>
          <p>
            <strong>Castle Point Innovations LLC</strong>
            <br />
            Email: <a href="mailto:contact@castlepointinnovations.com">contact@castlepointinnovations.com</a>
          </p>
        </>
      ),
    },
  ];

  return <PrivacyPolicy productName="ClickCV" lastUpdated="March 27, 2026" sections={sections} />;
}

export default ClickCVPrivacy;
